import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { redeemInvite } from "../lib/invites";

const STORAGE_REDEEMED = "quattro_invite_redeemed_v1";

/** Look at ?invite=XXX in the URL (one-time auto-fill). */
function readInviteFromUrl() {
  try {
    const u = new URL(window.location.href);
    return (u.searchParams.get("invite") || "").trim().toUpperCase();
  } catch { return ""; }
}

export function shouldOfferRedeem() {
  try { return localStorage.getItem(STORAGE_REDEEMED) !== "1"; } catch { return true; }
}

export function markRedeemed() {
  try { localStorage.setItem(STORAGE_REDEEMED, "1"); } catch {}
}

/**
 * Modal asking the user to enter (or auto-fill) an invite code.
 * On success calls onSuccess({chips_credited}) and self-marks as redeemed
 * so it never re-opens for this user.
 */
export default function RedeemInviteModal({ open, onClose, onSuccess }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    const fromUrl = readInviteFromUrl();
    if (fromUrl) setCode(fromUrl);
    setErr(""); setOkMsg("");
  }, [open]);

  const submit = async () => {
    if (busy || !code.trim()) return;
    setBusy(true); setErr(""); setOkMsg("");
    try {
      const res = await redeemInvite(code);
      markRedeemed();
      setOkMsg(`+${res.chips_credited} chips!`);
      setTimeout(() => {
        onSuccess?.(res);
        onClose?.();
      }, 1100);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Something went wrong";
      setErr(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          data-testid="redeem-invite-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 18 }} animate={{ scale: 1, y: 0 }}
            className="glass max-w-sm w-full p-7 rounded-3xl text-center relative"
            style={{ maxHeight: "92vh", overflowY: "auto" }}
            style={{ borderColor: "rgba(255,215,0,0.45)" }}
          >
            <div
              className="mx-auto mb-4 flex items-center justify-center rounded-full"
              style={{
                width: 60, height: 60,
                background: "linear-gradient(135deg, #FFD700, #FF6B6B)",
                fontSize: 24, color: "#0B0B0B",
                boxShadow: "0 0 28px rgba(255,215,0,0.45)",
              }}
            >🎁</div>

            <div className="label-eyebrow mb-2" style={{ color: "#FFD700", letterSpacing: 2 }}>
              FRIEND INVITE
            </div>
            <h2 className="font-display font-black text-2xl mb-2">Got a code?</h2>
            <p className="text-white/65 text-sm mb-5">
              Enter a friend's 6-character invite code and you both get
              <b style={{ color: "#FFD700" }}> +200 chips</b>.
            </p>

            {okMsg ? (
              <div
                className="font-display font-bold text-lg mb-4"
                style={{ color: "#3DDC97" }}
                data-testid="redeem-invite-success"
              >{okMsg}</div>
            ) : (
              <>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6));
                    if (err) setErr("");
                  }}
                  placeholder="ABCDEF"
                  className="w-full text-center font-mono font-black text-2xl tracking-[0.4em] rounded-xl px-4 py-3 mb-3 outline-none"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1.5px solid rgba(255,255,255,0.20)",
                    color: "#FFD700",
                  }}
                  maxLength={6}
                  autoFocus
                  data-testid="redeem-invite-input"
                />
                {err && (
                  <div className="text-xs mb-3" style={{ color: "#FF8A8A" }} data-testid="redeem-invite-error">
                    {err}
                  </div>
                )}
                <div className="flex gap-3 justify-center">
                  <button
                    className="btn-ghost"
                    onClick={onClose}
                    disabled={busy}
                    data-testid="redeem-invite-cancel"
                  >Skip</button>
                  <button
                    className="btn-primary"
                    onClick={submit}
                    disabled={busy || code.length < 4}
                    data-testid="redeem-invite-submit"
                  >{busy ? "Redeeming…" : "Redeem"}</button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
